import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import * as ProjectActions from './actions';
import { environment } from 'src/environments/environment';
import { IProject } from '@app/models/project';
import { HttpService } from '@surveyname/common';
import { Router } from '@angular/router';

@Injectable()
export class ProjectStoreEffects {
  constructor(
    private http: HttpService,
    private action$: Actions,
    private router: Router
  ) {}

  GetSelectedProject$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(ProjectActions.BeginGetSelectedProjectAction),
      mergeMap((action) =>
        this.http.get(`${environment.apiUrl}/project/${action.payload}`).pipe(
          map((project: IProject) => {
            return ProjectActions.SuccessGetSelectedProjectAction({
              payload: project,
            });
          }),
          catchError((error: Error) => {
            return of(ProjectActions.ErrorProjectsAction(error));
          })
        )
      )
    )
  );

  GetProjects$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(ProjectActions.GetProjectsAction),
      mergeMap((action) =>
        this.http.get(`${environment.apiUrl}/project`).pipe(
          map((projects: IProject[]) => {
            return ProjectActions.ProjectsLoadedAction({ payload: projects });
          }),
          catchError((error: Error) => {
            return of(ProjectActions.ErrorProjectsAction(error));
          })
        )
      )
    )
  );

  GetFilteredProjects$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(ProjectActions.GetFilteredProjectsAction),
      mergeMap((action) =>
        this.http
          .get(`${environment.apiUrl}/project`, { params: action.payload })
          .pipe(
            map((projects: IProject[]) => {
              return ProjectActions.FilteredProjectsLoadedAction({
                payload: projects,
              });
            }),
            catchError((error: Error) => {
              return of(ProjectActions.ErrorProjectsAction(error));
            })
          )
      )
    )
  );

  CreateProject$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(ProjectActions.BeginCreateProjectAction),
      mergeMap((action) => {
        return this.http
          .post(`${environment.apiUrl}/project`, action.payload)
          .pipe(
            map((project: IProject) => {
              return ProjectActions.SuccessCreateProjectAction({
                payload: project,
              });
            }),
            tap((result) =>
              this.router.navigate(['/project/detail', result.payload._id])
            ),
            catchError((error: Error) => {
              return of(ProjectActions.ErrorProjectsAction(error));
            })
          );
      })
    )
  );

  DeleteProject$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(ProjectActions.BeginDeleteProjectAction),
      mergeMap((action) =>
        this.http
          .delete(`${environment.apiUrl}/project/${action.payload}`)
          .pipe(
            map((project: IProject) => {
              return ProjectActions.SuccessDeleteProjectAction({
                payload: project._id,
              });
            }),
            catchError((error: Error) => {
              return of(ProjectActions.ErrorProjectsAction(error));
            })
          )
      )
    )
  );

  CloneProject$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(ProjectActions.BeginCloneProjectAction),
      mergeMap((action) =>
        this.http
          .post(`${environment.apiUrl}/project/clone`, action.payload)
          .pipe(
            map((project: IProject) => {
              return ProjectActions.SuccessCloneProjectAction({
                payload: project,
              });
            }),
            tap((result) =>
              this.router.navigate(['/project/detail', result.payload._id])
            ),
            catchError((error: Error) => {
              return of(ProjectActions.ErrorProjectsAction(error));
            })
          )
      )
    )
  );

  UpdateProject$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(ProjectActions.BeginUpdateProjectAction),
      mergeMap((action) =>
        this.http.put(`${environment.apiUrl}/project`, action.payload).pipe(
          map((project: IProject) => {
            return ProjectActions.SuccessUpdateProjectAction({
              payload: action.payload,
            });
          }),
          catchError((error: Error) => {
            return of(ProjectActions.ErrorProjectsAction(error));
          })
        )
      )
    )
  );
  UpdateLastUsedProject$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(ProjectActions.BeginUpdateLastUsedProjectAction),
      mergeMap((action) =>
        this.http.put(`${environment.apiUrl}/project`, action.payload).pipe(
          map((project: IProject) => {
            return ProjectActions.SuccessUpdateLastUsedProjectAction({
              payload: action.payload,
            });
          }),
          catchError((error: Error) => {
            return of(ProjectActions.ErrorProjectsAction(error));
          })
        )
      )
    )
  );

  UploadImage$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(ProjectActions.BeginUploadImageAction),
      mergeMap((action) => {
        console.log(action);
        const formData = new FormData();
        formData.append('projectId', action.payload.projectId);
        formData.append('fieldTechName', action.payload.fieldTechName);
        const files = action.payload.files;
        for (const file of files) {
          formData.append('picture', file);
        }
        return this.http
          .put(`${environment.apiUrl}/project/upload-image`, formData)
          .pipe(
            map((project: IProject) => {
              return ProjectActions.SuccessUploadImageAction({
                payload: project,
              });
            }),
            catchError((error: Error) => {
              return of(ProjectActions.ErrorProjectsAction(error));
            })
          );
      })
    )
  );
}
